import React, { useState } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { Table } from "reactstrap";
import { toast } from "react-toastify";

import { lang } from "../../../middleware/lang";
import { formatNumber } from "../../../middleware/common-functions";
import {
  GET_PAYMENT_LINKS,
  CREATE_PAYMENT_LINK,
  CANCEL_PAYMENT_LINK,
  RESEND_PAYMENT_LINK,
  GetPaymentLinksResponse,
  CreatePaymentLinkResponse,
  CancelPaymentLinkResponse,
  ResendPaymentLinkResponse,
} from "./queries";
import { PaymentLink } from "./models";

const statusConfig: Record<
  string,
  { label: string; labelEn: string; bg: string; color: string }
> = {
  pending: {
    label: "Pendiente",
    labelEn: "Pending",
    bg: "#FFF8E1",
    color: "#F9A825",
  },
  paid: {
    label: "Pagado",
    labelEn: "Paid",
    bg: "#E8F5E9",
    color: "#48BB78",
  },
  expired: {
    label: "Expirado",
    labelEn: "Expired",
    bg: "#FBE9E7",
    color: "#FF6028",
  },
  cancelled: {
    label: "Cancelado",
    labelEn: "Cancelled",
    bg: "#F5F5F5",
    color: "#999",
  },
};

const PaymentLinks: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerName, setCustomerName] = useState("");

  const { data, loading, refetch } = useQuery<GetPaymentLinksResponse>(
    GET_PAYMENT_LINKS,
    {
      variables: { take: 50 },
      fetchPolicy: "cache-and-network",
    }
  );

  const [createPaymentLink, { loading: creating }] =
    useMutation<CreatePaymentLinkResponse>(CREATE_PAYMENT_LINK);

  const [cancelPaymentLink] =
    useMutation<CancelPaymentLinkResponse>(CANCEL_PAYMENT_LINK);

  const [resendPaymentLink] =
    useMutation<ResendPaymentLinkResponse>(RESEND_PAYMENT_LINK);

  const links = data?.getPaymentLinks.data || [];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error(lang("Ingresa un monto valido", "Enter a valid amount"));
      return;
    }
    if (!description.trim()) {
      toast.error(
        lang("Ingresa un concepto", "Enter a description")
      );
      return;
    }
    if (!customerEmail.trim()) {
      toast.error(
        lang("Ingresa el email del cliente", "Enter the customer email")
      );
      return;
    }

    try {
      const result = await createPaymentLink({
        variables: {
          amount: parsedAmount,
          description: description.trim(),
          customer_email: customerEmail.trim(),
          customer_name: customerName.trim() || null,
        },
      });

      if (result.data?.createPaymentLink.emailSent) {
        toast.success(
          lang(
            "Link de cobro enviado por email",
            "Payment link sent by email"
          )
        );
      } else {
        toast.warning(
          lang(
            "Link creado pero no se pudo enviar el email",
            "Link created but email could not be sent"
          )
        );
      }

      setAmount("");
      setDescription("");
      setCustomerEmail("");
      setCustomerName("");
      setShowForm(false);
      refetch();
    } catch (err: any) {
      toast.error(err.message || lang("Error al crear link", "Error creating link"));
    }
  };

  const handleCancel = async (link: PaymentLink) => {
    if (
      !window.confirm(
        lang(
          `Cancelar el link de cobro por $${formatNumber(link.amount)} a ${link.customer_email}?`,
          `Cancel payment link for $${formatNumber(link.amount)} to ${link.customer_email}?`
        )
      )
    )
      return;

    try {
      await cancelPaymentLink({ variables: { id: parseInt(link.id) } });
      toast.success(lang("Link cancelado", "Link cancelled"));
      refetch();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleResend = async (link: PaymentLink) => {
    try {
      await resendPaymentLink({ variables: { id: parseInt(link.id) } });
      toast.success(lang("Email reenviado", "Email resent"));
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success(lang("Link copiado", "Link copied"));
  };

  return (
    <div>
      {/* Create button / form */}
      {!showForm ? (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <a
            href="https://foo-blog.web.app/posts/links-de-cobro.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#FFBB28", fontWeight: 600, fontSize: 13, textDecoration: "none" }}
          >
            <i className="fas fa-question-circle mr-1" />
            {lang("¿Cómo funciona?", "How does it work?")}
          </a>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="btn"
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: 8,
                padding: "8px 14px",
              }}
              onClick={() => refetch()}
              disabled={loading}
              title={lang("Actualizar", "Refresh")}
            >
              <i className={`fas fa-sync-alt${loading ? " fa-spin" : ""}`} />
            </button>
            <button
              className="btn"
              style={{
                background: "#FFBB28",
                color: "#292929",
                fontWeight: 600,
                borderRadius: 8,
                padding: "8px 20px",
              }}
              onClick={() => setShowForm(true)}
            >
              <i className="fas fa-plus mr-2" />
              {lang("Nuevo link de cobro", "New payment link")}
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ebebeb",
            borderRadius: 12,
            padding: "24px",
            marginBottom: 20,
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 style={{ margin: 0, fontWeight: 700 }}>
              {lang("Nuevo link de cobro", "New payment link")}
            </h5>
            <button
              className="btn btn-sm"
              onClick={() => setShowForm(false)}
              style={{ fontSize: 18, lineHeight: 1, color: "#999" }}
            >
              &times;
            </button>
          </div>

          <form onSubmit={handleCreate}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  {lang("Monto (MXN)", "Amount (MXN)")} *
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">$</span>
                  </div>
                  <input
                    type="number"
                    className="form-control"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="1"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  {lang("Concepto", "Description")} *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={lang(
                    "Ej: Pedido catering evento",
                    "E.g.: Catering order for event"
                  )}
                  required
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  {lang("Email del cliente", "Customer email")} *
                </label>
                <input
                  type="email"
                  className="form-control"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="cliente@email.com"
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label style={{ fontWeight: 600, fontSize: 13 }}>
                  {lang("Nombre del cliente", "Customer name")}{" "}
                  <span style={{ color: "#999", fontWeight: 400 }}>
                    ({lang("opcional", "optional")})
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder={lang("Nombre", "Name")}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end" style={{ gap: 10 }}>
              <button
                type="button"
                className="btn btn-sm"
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 6,
                  padding: "8px 20px",
                }}
                onClick={() => setShowForm(false)}
              >
                {lang("Cancelar", "Cancel")}
              </button>
              <button
                type="submit"
                className="btn btn-sm"
                disabled={creating}
                style={{
                  background: "#FFBB28",
                  color: "#292929",
                  fontWeight: 600,
                  borderRadius: 6,
                  padding: "8px 20px",
                }}
              >
                {creating ? (
                  <span className="spinner-border spinner-border-sm mr-2" />
                ) : (
                  <i className="fas fa-paper-plane mr-2" />
                )}
                {lang("Enviar link de cobro", "Send payment link")}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Links list */}
      {!loading && links.length === 0 && !showForm && (
        <div className="text-center py-5">
          <i
            className="fas fa-link fa-2x d-block mb-3"
            style={{ color: "#ccc" }}
          />
          <p className="text-muted">
            {lang(
              "No has enviado links de cobro aun",
              "You haven't sent any payment links yet"
            )}
          </p>
          <p className="text-muted" style={{ fontSize: 13 }}>
            {lang(
              "Crea un link para cobrar a tus clientes por email",
              "Create a link to charge your customers by email"
            )}
          </p>
          <a
            href="https://foo-blog.web.app/posts/links-de-cobro.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#FFBB28", fontWeight: 600, fontSize: 13, textDecoration: "none" }}
          >
            <i className="fas fa-question-circle mr-1" />
            {lang("¿Cómo funcionan los Links de Cobro?", "How do Payment Links work?")}
          </a>
        </div>
      )}

      {links.length > 0 && (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ebebeb",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <Table responsive hover className="mb-0">
            <thead>
              <tr style={{ background: "#f8f9fa" }}>
                <th>{lang("Fecha", "Date")}</th>
                <th>{lang("Cliente", "Customer")}</th>
                <th>{lang("Concepto", "Description")}</th>
                <th className="text-right">{lang("Monto", "Amount")}</th>
                <th className="text-center">{lang("Estado", "Status")}</th>
                <th className="text-center">{lang("Acciones", "Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => {
                const sc = statusConfig[link.status] || statusConfig.pending;
                return (
                  <tr key={link.id}>
                    <td style={{ whiteSpace: "nowrap", fontSize: 13 }}>
                      {new Date(link.created_at).toLocaleDateString("es-MX", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </td>
                    <td>
                      <div style={{ fontSize: 13 }}>
                        {link.customer_name && (
                          <div style={{ fontWeight: 600 }}>
                            {link.customer_name}
                          </div>
                        )}
                        <div style={{ color: "#666" }}>
                          {link.customer_email}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize: 13, color: "#666", maxWidth: 200 }}>
                      {link.description}
                    </td>
                    <td
                      className="text-right"
                      style={{ fontWeight: 600, fontSize: 14 }}
                    >
                      ${formatNumber(link.amount)}
                    </td>
                    <td className="text-center">
                      <span
                        style={{
                          display: "inline-block",
                          fontSize: 12,
                          padding: "3px 10px",
                          borderRadius: 6,
                          background: sc.bg,
                          color: sc.color,
                          fontWeight: 600,
                        }}
                      >
                        {lang(sc.label, sc.labelEn)}
                      </span>
                    </td>
                    <td className="text-center">
                      {link.status === "pending" && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          {link.checkout_url && (
                            <button
                              className="btn btn-sm"
                              title={lang("Copiar link", "Copy link")}
                              style={{
                                border: "1px solid #e0e0e0",
                                borderRadius: 6,
                                fontSize: 12,
                              }}
                              onClick={() =>
                                handleCopyLink(link.checkout_url!)
                              }
                            >
                              <i className="fas fa-copy" />
                            </button>
                          )}
                          <button
                            className="btn btn-sm"
                            title={lang("Reenviar email", "Resend email")}
                            style={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 6,
                              fontSize: 12,
                            }}
                            onClick={() => handleResend(link)}
                          >
                            <i className="fas fa-paper-plane" />
                          </button>
                          <button
                            className="btn btn-sm"
                            title={lang("Cancelar", "Cancel")}
                            style={{
                              border: "1px solid #e0e0e0",
                              borderRadius: 6,
                              fontSize: 12,
                              color: "#FF6028",
                            }}
                            onClick={() => handleCancel(link)}
                          >
                            <i className="fas fa-times" />
                          </button>
                        </div>
                      )}
                      {link.status === "paid" && link.paid_at && (
                        <span style={{ fontSize: 11, color: "#999" }}>
                          {new Date(link.paid_at).toLocaleDateString("es-MX", {
                            day: "2-digit",
                            month: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {loading && (
            <div className="text-center py-3">
              <span className="spinner-border spinner-border-sm" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentLinks;
